import type { ProblemItem } from '../api/financeTypes'
import { DASH, formatMoney } from '../lib/money'
import type { Tone } from './ui'

export const KIND_LABEL: Record<string, string> = {
  unknown_outcome: 'Ismeretlen kimenet',
  declined: 'Elutasított kártya',
  overdue: 'Tartozik',
  negative_credit: 'Negatív kredit',
  month_close_missing: 'Elmaradt zárás',
  card_expiring: 'Lejáró kártya',
  onetime_failed: 'Egyszeri vásárlás',
}

/** Display order of the filter chips (the design's order). */
export const KIND_ORDER = ['unknown_outcome', 'declined', 'overdue', 'negative_credit', 'month_close_missing', 'card_expiring', 'onetime_failed'] as const

export const SEVERITY: Record<string, { label: string; tone: Tone }> = {
  critical: { label: 'Kritikus', tone: 'crit' },
  warning: { label: 'Figyelem', tone: 'warn' },
  preventable: { label: 'Megelőzhető', tone: 'info' },
}

export const SOURCE_LABEL: Record<string, string> = {
  subscription: 'Előfizetés',
  onetime_product: 'Egyszeri termék',
  credit: 'Kredit-feltöltés',
  usage_fee: 'Használati díj (havi zárás)',
  other: 'Egyéb',
}

/** Row title for a problem item, e.g. "Ismeretlen kimenet – megújítás". */
export function problemTitle(i: ProblemItem): string {
  const base = KIND_LABEL[i.kind] ?? i.kind
  if (i.kind === 'unknown_outcome' || i.kind === 'declined') {
    const d = i.detail ?? ''
    const what = d.startsWith('renewal') ? 'megújítás' : d.startsWith('topup') ? 'feltöltés' : d
    const tail = /retries exhausted/.test(d) ? ' (próbálkozások kimerültek)' : ''
    return what ? `${base} – ${what}${tail}` : base
  }
  if (i.kind === 'month_close_missing' && i.detail) return `${base} (${i.detail})`
  return base
}

const HU_SHORT = new Intl.DateTimeFormat('hu-HU', { month: 'short', day: 'numeric', timeZone: 'UTC' })

/** `2026-10-10` -> `okt. 10.` (UTC, no local-timezone drift). */
export function shortDate(value: string | null | undefined): string {
  if (!value) return DASH
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value)
  return Number.isNaN(d.getTime()) ? value : HU_SHORT.format(d)
}

/** `2026-10-09T06:00:03Z` -> `2026-10-09 06:00`. */
export function shortStamp(value: string | null | undefined): string {
  if (!value) return DASH
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString().slice(0, 16).replace('T', ' ')
}

export function stampWithSeconds(value: string | null | undefined): string {
  if (!value) return DASH
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString().slice(0, 19).replace('T', ' ')
}

export const SUB_STATUS: Record<number, { label: string; tone: Tone }> = {
  0: { label: 'Nem indult', tone: 'mute' },
  1: { label: 'Aktív', tone: 'ok' },
  3: { label: 'Tartozik', tone: 'warn' },
  6: { label: 'Lemondva', tone: 'mute' },
}

export const COVER_FLAG: Record<string, { label: string; text: string }> = {
  balance_cover_insufficient: {
    label: 'Egyenleg-fedezet hiányos',
    text: 'A terhelés sikeres lett, de az egyeztetés a forrás-egyenleget kevesebbnek találta a tervezett levonásnál, ezért nem könyvelte. Kézi egyeztetés kell.',
  },
  balance_cover_unrecorded: {
    label: 'Egyenleg-levonás nincs rögzítve',
    text: 'A terhelés sikeres lett, de a tervezett egyenleg-levonások nincsenek rögzítve, ezért az egyeztetés nem könyvelte. Kézi egyeztetés kell.',
  },
}

/** `60,00 USD + 5,00 EUR`, or `nincs` for an empty list. */
export function moneyJoin(list: { currency: string; amount: string }[] | null | undefined): string {
  if (!list) return DASH
  return list.length === 0 ? 'nincs' : list.map((m) => formatMoney(m.amount, m.currency)).join(' + ')
}

/** A timeline event the API 0.12.0 marks as paid from the account balances. */
export const isBalancePaid = (title: string): boolean => /paid from balance/i.test(title)
